// Localização: ./src/hooks/useAIAssistant.js
// Hook customizado para comunicação com o assistente de IA

import { useCallback } from 'react';
import { useAI } from '../context/AIContext';
import { sendChatMessage, getConversations } from '../api/aiApi';
import { getErrorMessage } from '../utils/errorMessages';

/**
 * Hook para gerenciar interações com o assistente de IA
 */
export const useAIAssistant = () => {
    const {
        messages,
        sessionId,
        isLoading,
        error,
        pendingAction,
        addMessage,
        clearChat,
        updateSessionId,
        setLoading,
        setError,
        clearError,
        setPendingAction,
        clearPendingAction,
        loadConversation
    } = useAI();
    
    /**
     * Envia uma mensagem ao assistente
     */
    const sendMessage = useCallback(async (messageText) => {
        if (!messageText || messageText.trim() === '') {
            return;
        }
        
        // Adiciona mensagem do usuário imediatamente
        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: messageText,
            timestamp: new Date()
        };
        addMessage(userMessage);
        
        setLoading(true);
        clearError();
        
        try {
            const response = await sendChatMessage(messageText, sessionId);
            const data = response.data;
            
            // Atualiza sessionId se fornecido
            if (data.sessionId && data.sessionId !== sessionId) {
                updateSessionId(data.sessionId);
            }
            
            // Adiciona resposta da IA
            const aiMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: data.response,
                timestamp: new Date(),
                intent: data.intent,
                entities: data.entities,
                needsInfo: data.needsInfo,
                suggestedAction: data.suggestedAction,
                actionResult: data.actionResult
            };
            addMessage(aiMessage);
            
            // Define ação pendente se houver
            if (data.suggestedAction && !data.suggestedAction.ready) {
                setPendingAction(data.suggestedAction);
            } else if (data.suggestedAction?.ready) {
                clearPendingAction();
            }
            
            // Não adicionar mensagens de sistema aqui - a resposta do backend já contém a mensagem apropriada
            // As mensagens de sucesso e erro vêm diretamente do backend na resposta da IA
            
            return data;
            
        } catch (err) {
            console.error('[useAIAssistant] Erro ao enviar mensagem:', err);
            
            // Se a resposta contém uma mensagem de erro do backend, usar ela
            // Caso contrário, usar mensagem genérica ou amigável para erros de rede
            let errorMessage = 'Erro ao comunicar com o assistente. Tente novamente.';
            
            // Verificar se é erro de rede primeiro - usar mensagem amigável
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || !err.response) {
                errorMessage = getErrorMessage(err);
            } else if (err.response?.data?.response) {
                // Backend retornou erro como mensagem normal
                errorMessage = err.response.data.response;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                // Usar mensagem amigável se disponível
                errorMessage = getErrorMessage(err) || err.message;
            }
            
            // Não usar setError para evitar exibir no centro da tela
            // Apenas adicionar como mensagem do chat
            const errorMsg = {
                id: Date.now() + 1,
                role: 'assistant',
                content: errorMessage.startsWith('❌') ? errorMessage : `❌ ${errorMessage}`,
                timestamp: new Date(),
                type: 'error'
            };
            addMessage(errorMsg);
            
            // Não fazer throw para evitar que o erro seja tratado em outro lugar
            // O erro já foi adicionado como mensagem do chat
        } finally {
            setLoading(false);
        }
    }, [sessionId, addMessage, setLoading, clearError, updateSessionId, setPendingAction, clearPendingAction]);
    
    /**
     * Busca histórico de conversas
     */
    const fetchConversations = useCallback(async (params = {}) => {
        try {
            const response = await getConversations(params);
            return response.data;
        } catch (err) {
            console.error('[useAIAssistant] Erro ao buscar conversas:', err);
            throw err;
        }
    }, []);
    
    return {
        // Estado
        messages,
        sessionId,
        isLoading,
        error,
        pendingAction,
        
        // Ações
        sendMessage,
        clearChat,
        fetchConversations,
        clearError,
        clearPendingAction,
        loadConversation
    };
};

// Função removida - mensagens de sucesso/erro agora vêm diretamente do backend

